import { Box, Typography } from '@mui/material'
import { useApiVersion } from '../api/hooks'
import { Eyebrow, Mono, Surface } from '../components/primitives'
import { appVersion, type AppVersion } from '../version'

/**
 * What is running: this interface's build, and the API it is talking to.
 *
 * Reached only by typing /version — nothing links here — and open without
 * signing in, as the API's own GET /version is: neither says more than a
 * commit hash or a release tag.
 */
export function VersionPage() {
  const api = useApiVersion()
  return (
    <Box sx={{ minHeight: 'var(--pc-shell-height, 100vh)', display: 'grid', placeItems: 'center', p: 6 }}>
      <Surface sx={{ width: 520, p: 8 }}>
        <Eyebrow sx={{ mb: 2 }}>agribot · polygon checks</Eyebrow>
        <Typography variant="h2" sx={{ mb: 6 }}>Version</Typography>
        <Row label="Interface" version={appVersion()} />
        <Row
          label="API"
          version={api.data}
          note={api.isLoading ? 'Asking the API…' : api.isError ? `The API did not answer: ${(api.error as Error).message}` : undefined}
        />
      </Surface>
    </Box>
  )
}

function Row({ label, version, note }: { label: string; version: AppVersion | undefined; note?: string }) {
  return (
    <Box sx={{ mb: 5 }} data-testid={`version-${label.toLowerCase()}`}>
      <Typography variant="overline" sx={{ color: 'text.secondary' }}>{label}</Typography>
      {version ? (
        <>
          <Typography variant="h4" sx={{ wordBreak: 'break-all' }}>{version.version}</Typography>
          {/* The commit as well when the headline is a tag, so both are there to copy. */}
          {version.tag && version.commit && (
            <Mono sx={{ display: 'block', fontSize: 12, color: 'text.secondary', mt: 1, wordBreak: 'break-all' }}>
              {version.commit}
            </Mono>
          )}
        </>
      ) : (
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>{note ?? '—'}</Typography>
      )}
    </Box>
  )
}
